import { computed, ComputedRef, reactive, Ref, watch } from 'vue';
import { sleep } from '@libs/utils';

type PaneSizeKeyType = 'news' | 'symbol' | 'watchList' | 'technical' | 'assets';

type PanesSizeType = {
  [key in PaneSizeKeyType]: { size: number; minSize: number; maxSize?: number };
};

type UsePaneResizeParamsType = {
  bottomSectionMinSize: ComputedRef<number>;
  expandedNews: Ref<boolean>;
  expandedAssets: Ref<boolean>;
  showTechnicalChart: Ref<boolean>;
};

const usePaneResize = ({
  bottomSectionMinSize,
  expandedNews,
  expandedAssets,
  showTechnicalChart,
}: UsePaneResizeParamsType) => {
  const panesSize = reactive<PanesSizeType>({
    news: {
      minSize: bottomSectionMinSize.value,
      size: 40,
    },
    symbol: {
      minSize: 20,
      size: 60,
    },
    technical: {
      minSize: 20,
      size: 90,
    },
    assets: {
      size: 40,
      minSize: bottomSectionMinSize.value,
      maxSize: 80,
    },
    watchList: {
      minSize: 16,
      size: 60,
    },
  });

  //these numbers have been agreed upon with the design team.
  const assetSteps = computed(() => [
    panesSize.assets.minSize,
    17,
    40,
    panesSize.assets.maxSize as number,
  ]);

  watch(bottomSectionMinSize, async () => {
    panesSize.news.minSize = panesSize.assets.minSize =
      bottomSectionMinSize.value;

    await handleRightSectionSizes();
    await handleLeftSectionSizes();
  });

  const handleResize = (panes) => {
    panesSize.watchList.size = parseFloat(panes[0].size);

    if (showTechnicalChart.value) {
      panesSize.symbol.size = parseFloat(panes[1].size);
      panesSize.news.size = parseFloat(panes[2].size);
    } else {
      panesSize.news.size = parseFloat(panes[1].size);
    }
  };

  const handleResizeLeft = (panes) => {
    panesSize.assets.size = parseFloat(panes[1].size);
    if (showTechnicalChart.value) {
      panesSize.technical.size = parseFloat(panes[0].size);
    } else {
      panesSize.symbol.size = parseFloat(panes[0].size);
    }
  };

  //These codes below are for resizing as snap points.
  //This feature was developed but not used as needed.
  //The codes were kept until we needed a similar feature later.

  // const findClosestAssetPoint = () => {
  //   const assetSize = panesSize.assets.size;

  //   return assetSteps.value.reduce((a, b) => {
  //     return Math.abs(b - assetSize) < Math.abs(a - assetSize) ? b : a;
  //   });
  // };
  // const onAfterResizeLeft = () => {
  //   const closestAssetPoint = findClosestAssetPoint();
  //   const symbolOrTechnicalPoint = 100 - closestAssetPoint;
  //   panesSize.assets.size = closestAssetPoint;
  //   if (showTechnicalChart.value) {
  //     panesSize.technical.size = symbolOrTechnicalPoint;
  //   } else {
  //     panesSize.symbol.size = symbolOrTechnicalPoint;
  //   }
  // };

  const handleLeftSectionSizes = async () => {
    await sleep(10);

    if (expandedAssets.value) {
      if (showTechnicalChart.value) {
        panesSize.technical.size = 60;
        panesSize.assets.size = 40;
      } else {
        panesSize.symbol.size = 60;
        panesSize.assets.size = 40;
      }
    } else {
      if (showTechnicalChart.value) {
        panesSize.technical.size = 100 - bottomSectionMinSize.value;
        panesSize.assets.size = bottomSectionMinSize.value;
      } else {
        panesSize.symbol.size = 100 - bottomSectionMinSize.value;
        panesSize.assets.size = bottomSectionMinSize.value;
      }
    }
  };

  const handleRightSectionSizes = async () => {
    if (expandedNews.value) {
      if (showTechnicalChart.value) {
        panesSize.symbol.size = 50;
        panesSize.watchList.size = 20;
        panesSize.news.size = 30;
      } else {
        panesSize.watchList.size = 60;
        panesSize.news.size = 40;
      }
    } else {
      if (showTechnicalChart.value) {
        // We should make a change in size to prevent Splitpanes library from setting the size automatically
        panesSize.news.size += 1;

        await sleep(10);
        panesSize.symbol.size = 100 - 40 - bottomSectionMinSize.value;
        panesSize.watchList.size = 40;
        panesSize.news.size = bottomSectionMinSize.value;
      } else {
        await sleep(10);

        panesSize.watchList.size = 100 - bottomSectionMinSize.value;
        panesSize.news.size = parseInt(bottomSectionMinSize.value.toFixed(2));
      }
    }
  };

  watch([expandedAssets, showTechnicalChart], handleLeftSectionSizes);
  watch([expandedNews, showTechnicalChart], handleRightSectionSizes);

  return {
    panesSize,
    handleResize,
    handleResizeLeft,
  };
};

export default usePaneResize;
